import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LoadInfiniteHook,
  useGetDataHook,
  useSaveDataHook,
} from '@bymason/api-hooks-base';
import {
  CreateGroupOptions,
  GetGroupDeploymentsOptions,
  GetGroupDevicesOptions,
  ReadGroupOptions,
  ListGroupsOptions,
  GroupModel,
  groupsClient,
  MoveDevicesToGroupOptions,
} from '@bymason/controller-api';

export { GroupModel, GroupDeploymentsModel, MoveDevicesToGroupResult } from '@bymason/controller-api';

/**
 * React hook that asynchronously deletes a device group.
 * @returns HookArray
 * @HookArray [0] delete method
 * @HookArray [1] error
 * @HookArray [2] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 */
export function useDeleteGroup(clientToken: string) {
  const handleDeleteData = useCallback(
    (options: ReadGroupOptions) => {
      return groupsClient.deleteGroup(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleDeleteData);
}

/**
 * React hook that asynchronously gets paginated device groups.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetGroups(clientToken: string) {
  const handleGetData = useCallback(
    (options: ListGroupsOptions) => {
      return groupsClient.listGroups(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a device group.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetGroup(clientToken: string) {
  const handleGetData = useCallback(
    (options: ReadGroupOptions) => {
      return groupsClient.readGroup(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a device group's deployments.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 */
export function useGetGroupDeployments(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetGroupDeploymentsOptions) => {
      return groupsClient.getGroupDeployments(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a device group's devices.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 */
export function useGetGroupDevices(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetGroupDevicesOptions) => {
      return groupsClient.getGroupDevices(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously creates a device group.
 * @returns HookArray
 * @HookArray [0] save method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateGroup(clientToken: string) {
  const handleSaveData = useCallback(
    (options: CreateGroupOptions) => {
      return groupsClient.createGroup(options, clientToken);
    },
    [clientToken]
  );

  return useSaveDataHook(handleSaveData);
}

/**
 * React hook that asynchronously moves devices to a device group.
 * @returns HookArray
 * @HookArray [0] move method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useMoveDevicesToGroup(clientToken: string) {
  const handleMoveDevices = useCallback(
    (options: MoveDevicesToGroupOptions) => {
      return groupsClient.moveDevicesToGroup(options, clientToken);
    },
    [clientToken]
  );

  return useSaveDataHook(handleMoveDevices);
}

// TODO: generalize this hook into loadInfiniteDataHook hook generator, following
// the example of the getDataHook hook generator.

/**
 * React hook that loads paginated device groups "infinitely" via trigger,
 * for components that need to load more data on scroll or button click.
 * @returns HookArray
 * @HookArray [0] result
 * @HookArray [1] error
 * @HookArray [2] busy flag
 * @HookArray [3] page count
 * @HookArray [4] error code
 * @HookArray [5] clearData method
 */
export function useLoadInfiniteGroups(clientToken: string, limit = 50, loadMoreTrigger = false) {
  const [getData, data, error, busy, errorCode, errorData, clearData] = useGetGroups(clientToken);
  const [loadedData, setLoadedData] = useState<GroupModel[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [nextPageMarker, setNextPageMarker] = useState<string | undefined>();
  const blockTrigger = useRef(false);
  const pageCount = useRef(0);

  const resetLoadedData = useCallback(() => {
    clearData();
    setLoadedData([]);
    setHasMoreData(true);
    setNextPageMarker(undefined);
    blockTrigger.current = false;
    pageCount.current = 0;
    getData({ limit });
  }, [clearData, getData, limit]);

  // Fetch initial data
  useEffect(() => {
    resetLoadedData();
    // must have client token as dep. If it changes we
    // need to reset the loaded data.
  }, [resetLoadedData, clientToken]);

  // Fetch more data if conditions are right
  useEffect(() => {
    if (loadMoreTrigger && hasMoreData && !blockTrigger.current) {
      getData({ limit, after: nextPageMarker });
    }
  }, [getData, limit, loadMoreTrigger, hasMoreData, nextPageMarker]);

  // Process fetched data into loaded data and set flags
  useEffect(() => {
    if (data && !error) {
      blockTrigger.current = true;
      setLoadedData((prevData) => {
        if (prevData.length <= 5000) {
          const currData = data.data || [];
          return [...prevData, ...currData];
        }
        return prevData;
      });
      setHasMoreData(data.data !== null && !!data.nextPage);
      setNextPageMarker(data?.nextPage);
      pageCount.current++;
    }
    if (error) {
      setHasMoreData(false);
    }
  }, [data, error]);

  // Trigger block makes sure only one get event is triggered until the
  // trigger is reset. Scroll trigger doesn't reset to false after the data loads,
  // but eventually does reset with the next onScroll event.
  useEffect(() => {
    if (loadMoreTrigger === false) {
      blockTrigger.current = false;
    }
  });

  return [
    loadedData,
    error,
    busy,
    pageCount.current,
    errorCode,
    errorData,
    resetLoadedData,
  ] as LoadInfiniteHook<GroupModel[]>;
}
